/**
 * Read-only HTTP probes used by the meeting health dashboard.
 *
 * These deliberately do NOT reuse the typed entity helpers in `livekit.ts`,
 * `broadcasting.ts` or `meeting.ts`: those collapse every failure mode to
 * `null`, which is exactly the information a health view needs to keep. A
 * probe reports the HTTP status, the round trip time and the parsed body so
 * the dashboard can distinguish "no broadcast for this room" (404/204) from
 * "sync is down" (0/5xx) from "sync is slow" (200, high latency).
 */
import { Entity } from '../types/interfaces';
import { ConnectedUser } from '../stores/types/connection';
import { Broadcast } from '../stores/types/broadcasting';
import { HttpRequest } from '../stores/types/types';
import { buildSyncUrl, getAuthenticatedRequestInit, getSyncHeaders } from './utils';

/** Abandon a probe after this long so one hung service cannot stall a poll. */
export const PROBE_TIMEOUT_MS = 10000;

export interface ProbeResponse<T> {
	httpStatus: number; /** HTTP status, or 0 when the request never completed (network error/timeout/abort). */
	latencyMs: number; /** Round trip time in ms, measured client side. */
	body: T | null; /** Parsed JSON body, or null for empty/non-JSON responses. */
	error?: string; /** Populated only when the request itself failed. */
}

/**
 * Room session entity value written by sync when a greenroom session starts.
 * @see greenroom-microservices/sync `initRoomSession`
 */
export interface RoomSessionValue {
	sessionId: string;
	status: 'starting' | 'started' | 'closing' | 'none';
	room: number;
	closedAt?: number;
}

/**
 * OpenTok session entity value written by sync when it provisions the session.
 * @see greenroom-microservices/sync `createOpentokSessionAndSetEntity`
 */
export interface OpentokSessionValue {
	sessionId: string;
	region?: string;
}

/**
 * GETs a sync route and records status + latency instead of throwing.
 * Never rejects: a failed request comes back as `httpStatus: 0` with `error` set.
 */
export const probeSyncEndpoint = async <T>(
	route: string,
	token: string,
	/** Sending a request body switches the call to POST. */
	requestBody?: unknown,
): Promise<ProbeResponse<T>> => {
	const startedAt = performance.now();
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

	try {
		const response = await fetch(buildSyncUrl(route), {
			method: requestBody === undefined ? HttpRequest.Get : HttpRequest.Post,
			headers: getSyncHeaders(token),
			...(requestBody !== undefined && { body: JSON.stringify(requestBody) }),
			signal: controller.signal,
		});

		let body: T | null = null;
		try {
			const text = await response.text();
			body = text ? (JSON.parse(text) as T) : null;
		} catch {
			// 204s and sendStatus() responses have no JSON body - not an error here.
			body = null;
		}

		return {
			httpStatus: response.status,
			latencyMs: Math.round(performance.now() - startedAt),
			body,
		};
	} catch (error) {
		const aborted = (error as Error)?.name === 'AbortError';
		return {
			httpStatus: 0,
			latencyMs: Math.round(performance.now() - startedAt),
			body: null,
			error: aborted
				? `no response after ${PROBE_TIMEOUT_MS}ms`
				: (error as Error)?.message ?? 'request failed',
		};
	} finally {
		clearTimeout(timeout);
	}
};

/** Same probe semantics against any host rather than sync. */
export const probeUrl = async <T>(
	url: string,
	method: HttpRequest = HttpRequest.Get,
): Promise<ProbeResponse<T>> => {
	const startedAt = performance.now();
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

	try {
		// `Accept` alone is CORS-safelisted, so a GET needs no preflight. Adding
		// `Content-Type` forces one, and greenroom-socket answers it with Express's
		// default handler - no Access-Control-Allow-Headers, so the browser blocks.
		const headers: HeadersInit = method === HttpRequest.Get
			? { Accept: 'application/json' }
			: { Accept: 'application/json', 'Content-Type': 'application/json' };

		const response = await fetch(url, {
			method,
			headers,
			signal: controller.signal,
		});

		let body: T | null = null;
		try {
			const text = await response.text();
			body = text ? (JSON.parse(text) as T) : null;
		} catch {
			body = null;
		}

		return {
			httpStatus: response.status,
			latencyMs: Math.round(performance.now() - startedAt),
			body,
		};
	} catch (error) {
		const aborted = (error as Error)?.name === 'AbortError';
		return {
			httpStatus: 0,
			latencyMs: Math.round(performance.now() - startedAt),
			body: null,
			error: aborted
				? `no response after ${PROBE_TIMEOUT_MS}ms`
				: (error as Error)?.message ?? 'request failed',
		};
	} finally {
		clearTimeout(timeout);
	}
};

/** Sync's room session entity - the top level "is this room open" signal. */
export const ProbeRoomSession = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<Entity>(`v1/room/${roomId}/session`, token);

/**
 * Sync's OpenTok session entity.
 * Note: sync will provision a session here if the room session is open but no
 * OpenTok entity exists - a self healing path, not a read only route.
 */
export const ProbeOpentokSession = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<Entity>(`opentok/${roomId}`, token);

/**
 * Sync's broadcast entity. Sync refreshes this against Spielberg on read, so a
 * 200 here also proves Spielberg answered. 204 means no broadcast entity exists.
 */
export const ProbeBroadcast = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<Entity>(`v1/room/${roomId}/broadcast`, token);

/**
 * Connected users as greenroom-socket has persisted them to sync. This is the
 * server side view of the socket channel, not this browser's user list.
 */
export const ProbeConnectedUsers = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<ConnectedUser[]>(`v1/room/${roomId}/get-users`, token);

/**
 * Both unauthenticated. The override route returns the meeting's COMPLETE
 * effective flag set, not a sparse diff, so an override is only visible by
 * comparison against the defaults.
 */
export const ProbeFeatureFlagDefaults = () => probeUrl<Record<string, boolean>>(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flags`,
	HttpRequest.Post,
);

export const ProbeMeetingFeatureFlags = (
	meetingId: number,
) => probeUrl<Record<string, boolean>>(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/feature-flag-overrides/meeting/${meetingId}`,
	HttpRequest.Get,
);

/**
 * The dashboard-API answers **203** unauthenticated - that is healthy, not a
 * failure, so callers must accept any 2xx rather than only 200.
 */
export const ProbeDashboardApiHealth = () => probeUrl<{ status?: string }>(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/health-check`,
);

export const ProbeSocketHealth = () => probeUrl<unknown>(
	`${process.env.REACT_APP_SOCKET_HOST}/health-check`,
);

export interface SyncHealth {
	status: 'ok' | 'degraded';
	env?: string;
	uptimeSeconds: number;
	redis: { ok: boolean; latencyMs?: number; error?: string };
	socket: { ok: boolean; error?: string };
}

/**
 * Unauthenticated, so it stays preflight free. Answers **503** when sync is up
 * but a dependency is not, and the body names which - so read it, not just the
 * status.
 */
export const ProbeSyncHealth = () => probeUrl<SyncHealth>(buildSyncUrl('health-check'));

export interface GroundcoverLog {
	timestamp?: string;
	level?: string;
	[key: string]: any;
}

export interface LogsResponse {
	filters: LogFilter[];
	start: string;
	end: string;
	logs: GroundcoverLog[];
	limitReached: boolean;
}

/** ANDed together by sync, so each one only narrows. */
export interface LogFilter {
	key: string;
	value: string;
	op?: string;
}

/** The only workload sync will read. */
export const BROWSER_WORKLOAD: LogFilter = { key: 'workload', value: 'greenroom-browser' };

/**
 * Sync's own ceiling (`MAX_LIMIT` in its groundcover connection). Sent on every
 * query because sync otherwise falls back to 50, which a room of any size
 * exceeds in minutes - so the panel was reporting a truncated result almost
 * every time it was opened.
 */
export const LOG_LIMIT = 1000;

/**
 * Proxied by sync: Groundcover 404s the CORS preflight and the key is secret.
 * `sinceMs` counts back from now.
 *
 * Sync also accepts an explicit `start`/`end` pair, which would let this read a
 * show that has already finished rather than only a span ending now. Not wired
 * up here yet.
 */
export const ProbeLogs = (
	token: string,
	sinceMs: number,
	filters: LogFilter[] = [],
	limit: number = LOG_LIMIT,
) => probeSyncEndpoint<LogsResponse>('v1/logs/query', token, {
	sinceMs,
	limit,
	filters: [BROWSER_WORKLOAD, ...filters],
});

export type BroadcastEntityValue = Broadcast;

export interface ResolvedMeeting {
	roomId: number;
	slug: string;
	channel?: number;
	standaloneTitle?: string;
	eventUuid?: string | null;
	settings?: Record<string, any>;
}

/**
 * Resolves the slug from the url to the numeric room id the sync routes need.
 *
 * `/v2/greenroom/:meeting` accepts either form - it falls back to a lookup on
 * `gr_meetings.meeting_id` when the segment is not numeric - so this also keeps
 * working if someone pastes a numeric id.
 *
 * Deliberately not `LoadMeeting()`: that helper redirects the browser to a
 * no-access view when the room is missing or deleted, which would throw a
 * producer out of the dashboard instead of telling them what happened.
 */
export const ResolveMeeting = async (
	slug: string,
	token: string,
): Promise<{ httpStatus: number; meeting: ResolvedMeeting | null; error?: string }> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v2/greenroom/${encodeURIComponent(slug)}`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (!response.ok) {
			return { httpStatus: response.status, meeting: null };
		}

		const body = await response.json();
		// A deleted room still returns 200 with status 0.
		if (!body?.meeting || body.status === 0) {
			return { httpStatus: response.status, meeting: null };
		}

		// Routinely '{}' or absent on a room that has never saved settings.
		let settings: Record<string, any> | undefined;
		try {
			settings = body.settings_json ? JSON.parse(body.settings_json) : undefined;
		} catch {
			settings = undefined;
		}

		return {
			httpStatus: response.status,
			meeting: {
				roomId: Number(body.meeting),
				slug: body.meeting_id ?? slug,
				channel: body.channel,
				standaloneTitle: body.standalone_title,
				eventUuid: body.event_uuid ?? null,
				settings,
			},
		};
	} catch (error) {
		return {
			httpStatus: 0,
			meeting: null,
			error: (error as Error)?.message ?? 'request failed',
		};
	}
};
